import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { LegalFooter } from "../legal/Terms";

const NAV = [["/guides", "Guides"], ["/compare", "Compare"], ["/tools", "Free tools"], ["/pricing", "Pricing"]];

export function SeoNav() {
  return (
    <header className="border-b" style={{ borderColor: "#F0D3D8", background: "#FFFFFF" }}>
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
        <Link to="/" className="font-display text-lg font-extrabold tracking-tight" style={{ color: "#4A0F1A" }} data-testid="seo-nav-home">Capittance</Link>
        <nav className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:gap-5 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-600">
          {NAV.map(([to, label]) => (
            <Link key={to} to={to} className="hidden sm:inline hover:text-zinc-900 transition-colors" data-testid={`seo-nav-${label.toLowerCase().replace(/\s/g, "-")}`}>{label}</Link>
          ))}
          <Link to="/signup?plan=pro" className="px-3 py-2 rounded-sm text-white hover:opacity-90 transition-opacity" style={{ background: "#7A1F2B" }} data-testid="seo-nav-start">Start free trial</Link>
        </nav>
      </div>
    </header>
  );
}

export function SeoHero({ eyebrow, h1, intro, children }) {
  return (
    <div style={{ background: "linear-gradient(135deg, #4A0F1A 0%, #7A1F2B 55%, #B03A48 100%)" }}>
      <div className="max-w-6xl mx-auto px-6 py-14 md:py-20 text-white">
        {eyebrow && <div className="font-mono text-[11px] uppercase tracking-[0.24em]" style={{ color: "#F0C7CE" }}>{eyebrow}</div>}
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mt-4 max-w-4xl" style={{ color: "#FFFFFF" }} data-testid="seo-hero-h1">{h1}</h1>
        {intro && <p className="mt-5 text-base md:text-lg max-w-3xl leading-relaxed" style={{ color: "#F3D6DB" }}>{intro}</p>}
        {children}
      </div>
    </div>
  );
}

const PERKS = ["Testing, cutover and hypercare control tower", "C-Suite dashboard with live RAG and RAID", "Board pack, PDF and PowerPoint exports", "NAVBOT AI answers from your own programme data"];

export function PlanCTA({ id = "default" }) {
  return (
    <section className="mt-16 p-8 md:p-10 rounded-md text-white" style={{ background: "linear-gradient(135deg, #4A0F1A 0%, #7A1F2B 100%)" }} data-testid={`plan-cta-${id}`}>
      <div className="grid md:grid-cols-[1.3fr_1fr] gap-8 items-center">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "#F0C7CE" }}>Run it live in Capittance</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold mt-2" style={{ color: "#FFFFFF" }}>Stop rebuilding status in spreadsheets.</h2>
          <ul className="mt-4 space-y-2 text-sm" style={{ color: "#F3D6DB" }}>
            {PERKS.map((p) => <li key={p} className="flex items-start gap-2"><Check size={15} className="mt-0.5 shrink-0" style={{ color: "#C9A66B" }}/>{p}</li>)}
          </ul>
        </div>
        <div className="flex flex-col gap-3">
          <Link to="/signup?plan=pro" className="inline-flex items-center justify-between gap-2 bg-white px-5 py-4 rounded-sm font-mono text-[11px] font-bold uppercase tracking-[0.18em] hover:translate-x-1 transition-transform" style={{ color: "#7A1F2B" }} data-testid={`plan-cta-${id}-pro`}>
            <span>Platinum · $350/mo · 14 days free</span><ArrowRight size={14}/>
          </Link>
          <Link to="/signup?plan=starter" className="inline-flex items-center justify-between gap-2 border border-white/60 px-5 py-4 rounded-sm font-mono text-[11px] font-bold uppercase tracking-[0.18em] hover:bg-white/10 transition-colors" data-testid={`plan-cta-${id}-starter`}>
            <span>Starter · $200/mo</span><ArrowRight size={14}/>
          </Link>
          <Link to="/pricing" className="text-xs underline underline-offset-2 text-center" style={{ color: "#F0C7CE" }} data-testid={`plan-cta-${id}-pricing`}>Compare plans</Link>
        </div>
      </div>
    </section>
  );
}

export function SeoShell({ children }) {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SeoNav />
      {children}
      <div className="max-w-6xl mx-auto px-6 pb-10"><LegalFooter /></div>
    </div>
  );
}

export function Breadcrumb({ items }) {
  return (
    <nav className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-6" aria-label="Breadcrumb">
      {items.map(([to, label], i) => (
        <span key={label}>{i > 0 && " / "}{to ? <Link to={to} className="hover:text-zinc-900">{label}</Link> : <span className="text-zinc-800">{label}</span>}</span>
      ))}
    </nav>
  );
}

export const crumbsLd = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map(([to, label], i) => ({ "@type": "ListItem", position: i + 1, name: label, item: `https://capittance.com${to || ""}` })),
});
